import { useEffect, useState } from "react";
import { api, type InvoiceSettings } from "../lib/api";
import { navigate } from "../lib/route";

function previewFormat(fmt: string): string {
  const now = new Date();
  const year4 = String(now.getFullYear());
  const year2 = year4.slice(2);
  const month = String(now.getMonth() + 1).padStart(2, "0");
  return fmt
    .replace(/%Y/g, year4)
    .replace(/%y/g, year2)
    .replace(/%m/g, month)
    .replace(/%N/g, "01")
    .replace(/%n/g, "001");
}

const EMPTY: InvoiceSettings = {
  business_name: "",
  business_address: "",
  business_city: "",
  business_country: "",
  kvk_number: "",
  btw_number: "",
  number_format: "",
};

export function InvoiceSettingsPage() {
  const [settings, setSettings] = useState<InvoiceSettings>(EMPTY);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    void (async () => {
      try {
        const s = await api.getInvoiceSettings();
        setSettings(s);
      } catch (e: unknown) {
        setLoadError(e instanceof Error ? e.message : "Instellingen laden mislukt");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  function set<K extends keyof InvoiceSettings>(key: K, value: InvoiceSettings[K]) {
    setSaved(false);
    setSettings((prev) => ({ ...prev, [key]: value }));
  }

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setSaveError(null);
    setSaved(false);
    try {
      const updated = await api.updateInvoiceSettings(settings);
      setSettings(updated);
      setSaved(true);
    } catch (e: unknown) {
      setSaveError(e instanceof Error ? e.message : "Opslaan mislukt");
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <div className="page-container" aria-busy="true" />;
  if (loadError) return (
    <div className="page-container">
      <p style={{ color: "var(--danger)" }}>{loadError}</p>
    </div>
  );

  return (
    <div className="page-container">
      <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", marginBottom: "1.5rem", flexWrap: "wrap" }}>
        <button
          className="btn-ghost"
          style={{ fontSize: "0.875rem", padding: "0.35rem 0.75rem" }}
          onClick={() => navigate('/app/invoices')}
        >
          ← Facturen
        </button>
        <h1 style={{ margin: 0, fontSize: "1.25rem", fontWeight: 700 }}>Factuurinstellingen</h1>
      </div>

      <div style={{
        background: "var(--surface)",
        border: "1px solid var(--border)",
        borderRadius: "14px",
        padding: "1.5rem",
        boxShadow: "var(--shadow)",
        maxWidth: "600px",
      }}>
        <form onSubmit={handleSave} style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>

          <div className="form-group" style={{ marginBottom: 0 }}>
            <label style={{ display: "block", marginBottom: "0.35rem", fontSize: "0.875rem", fontWeight: 500 }}>
              Bedrijfsnaam
            </label>
            <input
              value={settings.business_name}
              onChange={(e) => set("business_name", e.target.value)}
              placeholder="Mijn Fotografie BV"
            />
          </div>

          <div className="form-group" style={{ marginBottom: 0 }}>
            <label style={{ display: "block", marginBottom: "0.35rem", fontSize: "0.875rem", fontWeight: 500 }}>
              Adres
            </label>
            <input
              value={settings.business_address}
              onChange={(e) => set("business_address", e.target.value)}
              placeholder="Straatnaam 1"
            />
          </div>

          <div className="form-group" style={{ marginBottom: 0 }}>
            <label style={{ display: "block", marginBottom: "0.35rem", fontSize: "0.875rem", fontWeight: 500 }}>
              Stad
            </label>
            <input
              value={settings.business_city}
              onChange={(e) => set("business_city", e.target.value)}
              placeholder="Amsterdam"
            />
          </div>

          <div className="form-group" style={{ marginBottom: 0 }}>
            <label style={{ display: "block", marginBottom: "0.35rem", fontSize: "0.875rem", fontWeight: 500 }}>
              Land
            </label>
            <input
              value={settings.business_country}
              onChange={(e) => set("business_country", e.target.value)}
              placeholder="Nederland"
            />
          </div>

          <div className="form-group" style={{ marginBottom: 0 }}>
            <label style={{ display: "block", marginBottom: "0.35rem", fontSize: "0.875rem", fontWeight: 500 }}>
              KVK-nummer
            </label>
            <input
              value={settings.kvk_number}
              onChange={(e) => set("kvk_number", e.target.value)}
              placeholder="12345678"
            />
          </div>

          <div className="form-group" style={{ marginBottom: 0 }}>
            <label style={{ display: "block", marginBottom: "0.35rem", fontSize: "0.875rem", fontWeight: 500 }}>
              BTW-nummer
            </label>
            <input
              value={settings.btw_number}
              onChange={(e) => set("btw_number", e.target.value)}
              placeholder="NL000000000B01"
            />
          </div>

          <div className="form-group" style={{ marginBottom: 0 }}>
            <label style={{ display: "block", marginBottom: "0.35rem", fontSize: "0.875rem", fontWeight: 500 }}>
              Factuurnummer formaat
            </label>
            <input
              value={settings.number_format}
              onChange={(e) => set("number_format", e.target.value)}
              placeholder="%Y-%n"
            />
            <small style={{ display: "block", marginTop: "0.35rem", color: "var(--muted)", lineHeight: 1.5 }}>
              Beschikbare tokens: <code>%Y</code> (jaar 4 cijfers), <code>%y</code> (jaar 2 cijfers), <code>%m</code> (maand), <code>%n</code> (volgnummer/jaar), <code>%N</code> (volgnummer/maand)
            </small>
            {settings.number_format && (
              <div style={{
                marginTop: "0.5rem",
                padding: "0.5rem 0.75rem",
                background: "var(--surface-raised)",
                border: "1px solid var(--border)",
                borderRadius: "6px",
                fontSize: "0.875rem",
                color: "var(--text-secondary)",
              }}>
                Voorbeeld: <strong style={{ color: "var(--text)" }}>{previewFormat(settings.number_format)}</strong>
              </div>
            )}
          </div>

          {saveError && (
            <p style={{ color: "var(--danger)", margin: 0, fontSize: "0.875rem" }}>{saveError}</p>
          )}
          {saved && (
            <p style={{ color: "var(--success)", margin: 0, fontSize: "0.875rem", fontWeight: 500 }}>Opgeslagen</p>
          )}

          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <button type="submit" className="btn-primary" disabled={saving}>
              {saving ? "Opslaan…" : "Opslaan"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
